/**
 Fxxl Page Context Reactor

 Filename: page_context_reactor.js

 @category  Fahrradxxl
 @package   Module
 @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 @copyright 2024 Fahrrad-xxl.de

 This Class was born out of the need to check for specific URL Params
 at the earliest time possible in JS and possibly set system-wide variables
 to be used at given times. ( Basically "reacting" to things in the URL. )

 Example:
    Sometimes the URL contains utm_ Params like `utm_source`, `utm_campaign`, etc.
    Those get lost with switching to the next page, but the information is still valuable
    for the whole user session, because it tells a user came from a specific place,
    for instance a newsletter email.

 This Class should mostly serve as system-wide helper, so no initialisation is intended.
 */

import FxxlCore  from '@fxxl/core';
import FxxlError from '@fxxl/error';
import FxxlURL   from '@fxxl/fxxl_url';


// FxxlPageContext aims to be a Singelton
export default class FxxlPageContext {
    /* Static Variables - GLOBALS
 * --------------------------------------------------------------------- */
    static ERROR_MESSAGES_PREFIX = 'FxxlPageContext';
    static get ERROR_MESSAGE() {
        return {
            INSTANCE_EXISTS: `${FxxlPageContext.ERROR_MESSAGES_PREFIX}: Call: An Instance exists`,
        };
    }

    /* Static Variables - CONFIG
     * --------------------------------------------------------------------- */
    static get CONFIG() {
        const storageKeyUtmNewsletter = FxxlCore.getConfig()?.storageKeys?.utmSourceNewsletter;

        return {
            STORAGE: {
                UTM_NEWSLETTER: {
                    KEY: storageKeyUtmNewsletter,
                    CONTENT: FxxlCore.getStorage(storageKeyUtmNewsletter),
                    EXPIRATION_TIME: Date.now() + (20 * 365 * 24 * 60 * 60 * 1000) // 20 Jahre
                }
            }
        };
    }

    /**
     * Check if the current URL contains the Param "utm_source=newsletter"
     *
     * @param url ( No need to create a new FxxlURL Instance, if one exists already )
     * @returns {boolean}
     */
    static urlContainsUTMSourceNewsletter(url = false) {
        let currentUrl = url;

        if (!currentUrl) {
            currentUrl = FxxlURL.getCurrent();
        }

        const urlSearchParams = currentUrl?.nativeUrl?.searchParams;
        return urlSearchParams?.get('utm_source') === 'newsletter';
    }

    /* Constructor
     * --------------------------------------------------------------------- */
    constructor() {
        // Exit Criteria: Only one Instance acceptable
        if (FxxlPageContext._instance) {
            new FxxlError(FxxlPageContext.ERROR_MESSAGE.INSTANCE_EXISTS);
            return;
        }

        // Bind: Instance
        FxxlPageContext._instance = this;

        // Get Config
        this.CONFIG = FxxlPageContext.CONFIG;
    }

    /* Private / Public Methods - React to Page Context
     * ------------------------------------------------------------------------ */
    /**
     * Serves as the main entry point for reacting to Page Context
     */
    react() {
        // URL Handlers
        this.handleURLUTMSourceNewsletter();
    }


    /* Private / Public Methods - React to Page Context -> URL
     * ------------------------------------------------------------------------ */
    /**
     * Examines the URL Part of the Page Context
     * and based on if the URL contains "utm_source=newsletter"
     * a LocalStorage Variable will be build with Content from the URL Paramas
     */
    handleURLUTMSourceNewsletter() {
        if (!FxxlPageContext.urlContainsUTMSourceNewsletter()) {
            return;
        }

        // Storage Config
        const storageKeyName    = this.CONFIG.STORAGE.UTM_NEWSLETTER.KEY;
        const storageExpireDate = this.CONFIG.STORAGE.UTM_NEWSLETTER.EXPIRATION_TIME;

        // Check URL
        const url = FxxlURL.getCurrent();
        const urlSearchParams = url?.nativeUrl?.searchParams;
        const data = {
            utmId:       urlSearchParams?.get('utm_id')         || '',
            utmSource:   urlSearchParams?.get('utm_source')     || '',
            utmMedium:   urlSearchParams?.get('utm_medium')     || '',
            utmCampaign: urlSearchParams?.get('utm_campaign')   || '',
            utmTerm:     urlSearchParams?.get('utm_term')       || '',
            utmContent:  urlSearchParams?.get('utm_content')    || '',
        }

        // Set Local Storage ( Overwriting currently is intended )
        FxxlCore.setStorage(storageKeyName, data, storageExpireDate);
    }
}